import { PageId } from 'constants/pages'
import i18n from 'i18next'
import { isEmpty } from 'lodash'
import {
  PaymentSystem,
  PIT_PAT_UPSELL_FLOW_KEYS,
} from 'nove8/constants/payment'
import { TStep } from 'nove8/types'
import { Countries, LOCALES } from 'root-constants'
import { StatesConfig } from 'xstate'
import { useZustandGlobalLocalStore } from 'zustandGlobalLocalStore'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandGlobalStore } from 'zustandGlobalStore'
import {
  NO_ANSWER,
  NONE_ANSWER,
} from 'nove8/cohorts/woofz_rt/pages/questions/constants'
import { TAnswers } from 'models/common.model'
import { getAnswer } from 'nove8/helpers/getAnswer'
import { getHasBasicSubscription } from '../../helpers/getHasBasicSubscription'

const getHasEmail = () => !!useZustandGlobalStore.getState().lead?.email

const getHasSubscription = () =>
  getHasBasicSubscription(
    useZustandGlobalStore.getState().lead?.subscriptions || null,
  )
const getHasAccount = () =>
  !!useZustandGlobalStore.getState().lead?.firebaseToken
const getHasAnswers = () =>
  !isEmpty(
    useZustandSessionStore.getState().userAnswers ||
      useZustandGlobalStore.getState().lead?.quizzes ||
      null,
  )
const isPitPatUpsellAvailable = () =>
  i18n.language === LOCALES.EN &&
  useZustandGlobalStore.getState().lead?.country === Countries.GB &&
  PIT_PAT_UPSELL_FLOW_KEYS.includes(
    useZustandGlobalStore
      .getState()
      .firebaseRemoteConfig?.getValue('upsell_flow_type')
      ?.asString() as string,
  )
const getAnswers = (): Partial<TAnswers> | null =>
  useZustandSessionStore.getState().userAnswers
const shouldSkipOnePageOnDogActAnswer = () => {
  const savedAnswers = getAnswers()
  if (!savedAnswers) return false
  const dogActAnswer = getAnswer(savedAnswers[PageId.DOG_ACT_RT_RTV2] as string)
  if (dogActAnswer === NO_ANSWER) {
    return true
  }
  return false
}
const shouldPreviousSkipFromAggressiveFoodSchedule = () => {
  const savedAnswers = getAnswers()
  if (!savedAnswers) return false
  const raw = savedAnswers[PageId.ACCESS_TO_FOOD_RTV2]
  if (raw == null) return false
  const v = getAnswer(raw as string)
  return v === NO_ANSWER
}
const shouldPreviousSkipFromWalksFeelChallengesNone = () => {
  const savedAnswers = getAnswers()
  if (!savedAnswers) return false
  const raw = savedAnswers[PageId.CHALLENGES_RT_RTV2]
  if (raw == null) return false
  return getAnswer(raw as string) === NONE_ANSWER
}
const isPaypalPayment = () =>
  ((useZustandGlobalStore.getState().lead?.subscriptions?.[0]
    ?.paymentSystemType as unknown) as PaymentSystem) === PaymentSystem.PAYPAL

export const getXStateConfigWoofzRt = (
  pages: TStep[],
): StatesConfig<any, any, any> => ({
  initial: {
    always: [
      {
        cond: (): boolean => !!useZustandGlobalLocalStore.getState().purchaseId,
        target:
          useZustandGlobalLocalStore.getState().purchasePageId ||
          pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const url = new URL(window.location.href)
          const pageId = url.pathname.split('/').pop()
          const isNewsletterPage = pageId === PageId.NEWSLETTER_URGE_RTV2

          return !hasEmail && isNewsletterPage
        },
        target: pages.find((p) => p.pageId.includes('email'))?.pageId,
      },
      // Do not redirect users to the next breakpoint before email page
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const hasSubscription = getHasSubscription()
          const hasAccount = getHasAccount()
          const hasAnswers = getHasAnswers()
          return !hasEmail && !hasSubscription && !hasAccount && hasAnswers
        },
        target:
          pages.find((dynamicPage) => {
            const pageIdFromUrl =
              window.location.pathname.split('/').pop() || ''
            return pageIdFromUrl === dynamicPage.pageId
          })?.pageId || pages[0].pageId,
      },
      {
        cond: (): boolean => getHasAccount(),
        target: PageId.DOWNLOAD_RTV2,
      },
      {
        cond: (): boolean => getHasSubscription(),
        target: PageId.ACCOUNT_NOVE8,
      },
      {
        cond: (): boolean => getHasEmail(),
        target: PageId.SUBSCRIPTIONS_RT_RTV2,
      },
      {
        target: pages[0].pageId,
      },
    ],
  },
  ...pages.reduce(
    (acc, page, index) => ({
      ...acc,
      [page.pageId]: {
        on: {
          NEXT_FLOW_STEP: {
            target: pages[index + 1]?.pageId,
          },
          SKIP_ONE_STEP: {
            target: pages[index + 2]?.pageId,
          },
          PREVIOUS_FLOW_STEP: {
            target:
              index - 1 >= 0 ? pages[index - 1].pageId : pages[index].pageId,
          },
          SKIP_ONE_STEP_BACK: {
            target:
              index - 2 >= 0 ? pages[index - 2].pageId : pages[index].pageId,
          },
          ...(page.pageId === PageId.DOG_ACT_RT_RTV2 && {
            NEXT_FLOW_STEP: [
              {
                cond: shouldSkipOnePageOnDogActAnswer,
                target: PageId.ACCESS_TO_FOOD_RTV2,
              },
              {
                target: PageId.DOG_OFF_AT_HOME_RTV2,
              },
            ],
          }),
          ...(page.pageId === PageId.ACCESS_TO_FOOD_RTV2 && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: shouldSkipOnePageOnDogActAnswer,
                target: PageId.DOG_ACT_RT_RTV2,
              },
              {
                target: PageId.URGE_RT_6_RTV2,
              },
            ],
          }),
          ...(page.pageId === PageId.EMAIL_RT_RTV2 && {
            PREVIOUS_FLOW_STEP: [
              {
                target: PageId.TRAINING_RT_RTV2,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_STATISTICS_RT_RTV2 && {
            PREVIOUS_FLOW_STEP: [
              {
                target: PageId.URGE_RT_10_RTV2,
              },
            ],
          }),
          ...(page.pageId === PageId.WALKS_FEEL_RTV2 && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: shouldPreviousSkipFromWalksFeelChallengesNone,
                target: PageId.URGE_RT_V1_RTV2,
              },
              {
                target: PageId.CHALLENGES_RT_RTV2,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_AGGRESSIVE_RT_RTV2 && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: shouldPreviousSkipFromAggressiveFoodSchedule,
                target: PageId.ACCESS_TO_FOOD_RTV2,
              },
              {
                target: PageId.URGE_RT_7_RTV2,
              },
            ],
          }),
          ...(page.pageId === PageId.SUBSCRIPTIONS_RT_RTV2 && {
            NEXT_FLOW_STEP: [
              {
                cond: isPaypalPayment,
                target: PageId.ACCOUNT_NOVE8,
              },
              {
                target: PageId.CHAT_UPSELL_RTV2,
              },
            ],
          }),
          ...(page.pageId === PageId.CHAT_UPSELL_RTV2 && {
            NEXT_FLOW_STEP: PageId.UPSELL_1_RTV2,
          }),
          ...(page.pageId === PageId.UPSELL_1_RTV2 && {
            NEXT_FLOW_STEP: [
              {
                cond: isPitPatUpsellAvailable,
                target: PageId.UPSELL_PITPAT_RTV2,
              },
              {
                target: PageId.ACCOUNT_NOVE8,
              },
            ],
          }),
        },
      },
    }),
    {},
  ),
  [PageId.ACCOUNT_NOVE8]: {
    on: {
      NEXT_FLOW_STEP: PageId.DOWNLOAD_RTV2,
    },
  },
  [PageId.DOWNLOAD_RTV2]: {
    type: 'final',
  },
})
